import { PageIds } from '@config';
/* eslint-disable @typescript-eslint/no-unused-vars */
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { environment } from '@environment';
import { easyclaHelp, pageSettings } from '@config';
import { CommunityInitiative, Foundation } from '@models';
import {
  AuthService,
  // CrowdfundingService,
  FoundationService,
  IntercomService,
  NotificationsService,
  UserService,
} from '@services';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { generalConstants } from '@config';

declare global {
  interface Window {
    DD_RUM: any;
  }
}

@Component({
  selector: 'lfx-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  title = 'lfx';
  pageSettings;
  pageHasScroll;
  helpUrl: string;
  myCommunityInitiatives$: Observable<CommunityInitiative[]>;
  private intercomBootAttempted = false;
  constructor(
    public authService: AuthService,
    public userService: UserService,
    private notificationService: NotificationsService,
    private route: ActivatedRoute,
    private router: Router,
    private foundationService: FoundationService,
    private intercomService: IntercomService,
    // private crowdfundingService: CrowdfundingService,
  ) {
    this.pageSettings = pageSettings;
    this.pageSettings.pageWithFooter = true;

    this.setUserInLFxHeader();
  }

      ngOnInit() {
      this.userService.initializeFavourite();
      // this.myCommunityInitiatives$ = this.crowdfundingService
      //   .getMyInitiatives()
      //   .pipe(catchError(() => of(null)));
    const script = document.createElement('script');

    script.setAttribute('src', environment.lfxHeaderUrl);
    document.head.appendChild(script);
    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        switchMap(() => this.getRouteHelpPath())
      )
      .subscribe(url => this.setHelpPath(url));
    this.initLFXHeaderLinks();
  }

  // set page right collapse
  onToggleSidebarRight(val: boolean): void {
    if (this.pageSettings.pageSidebarTwo) {
      this.pageSettings.pageSidebarTwo = false;
    } else {
      this.pageSettings.pageSidebarTwo = true;
    }
  }

  // hide mobile sidebar
  onHideMobileSidebar(val: boolean): void {
    if (this.pageSettings.pageMobileSidebarToggled) {
      if (this.pageSettings.pageMobileSidebarFirstClicked) {
        this.pageSettings.pageMobileSidebarFirstClicked = false;
      } else {
        this.pageSettings.pageMobileSidebarToggled = false;
      }
    }
  }

  // toggle mobile sidebar
  onToggleMobileSidebar(val: boolean): void {
    if (this.pageSettings.pageMobileSidebarToggled) {
      this.pageSettings.pageMobileSidebarToggled = false;
    } else {
      this.pageSettings.pageMobileSidebarToggled = true;
      this.pageSettings.pageMobileSidebarFirstClicked = true;
    }
  }

  // hide right mobile sidebar
  onHideMobileRightSidebar(val: boolean): void {
    if (this.pageSettings.pageMobileRightSidebarToggled) {
      if (this.pageSettings.pageMobileRightSidebarFirstClicked) {
        this.pageSettings.pageMobileRightSidebarFirstClicked = false;
      } else {
        this.pageSettings.pageMobileRightSidebarToggled = false;
      }
    }
  }

  // toggle right mobile sidebar
  onToggleMobileRightSidebar(val: boolean): void {
    if (this.pageSettings.pageMobileRightSidebarToggled) {
      this.pageSettings.pageMobileRightSidebarToggled = false;
    } else {
      this.pageSettings.pageMobileRightSidebarToggled = true;
      this.pageSettings.pageMobileRightSidebarFirstClicked = true;
    }
  }

  setUserInLFxHeader(): void {
    const lfHeaderElement: any = document.getElementById('lfx-header');

    if (!lfHeaderElement) {
      return;
    }

    lfHeaderElement.beforeLogout = () => {
      this.intercomService.shutdown();
      this.authService.clearSession();
    };

    if (this.authService.userProfile$) {
      this.authService.userProfile$.subscribe(data => {
        if (data) {
          lfHeaderElement.authuser = data;

          // Only call DD_RUM if it's available (DataDog script loaded)
          if (window.DD_RUM?.onReady) {
            window.DD_RUM.onReady(function () {
              window.DD_RUM.setUser({
                id: data['https://sso.linuxfoundation.org/claims/username'],
              });
            });
          }

          // Boot Intercom with Auth0 user data
          if (!this.intercomBootAttempted && environment.intercomId) {
            const intercomJwt = data[environment.auth0IntercomClaim];
            const userId = data[environment.auth0UsernameClaim];

            if (userId && intercomJwt) {
              this.intercomBootAttempted = true;
              this.intercomService
                .boot({
                  api_base: environment.intercomApiBase,
                  app_id: environment.intercomId,
                  intercom_user_jwt: intercomJwt,
                  user_id: userId,
                  name: [data.firstName, data.lastName].filter(Boolean).join(' '),
                  email: data.primaryEmail,
                })
                .catch((error: any) => {
                  console.error('AppComponent: Failed to boot Intercom', error);
                  this.intercomBootAttempted = false;
                });
            } else {
              console.warn('AppComponent: Intercom not booted — missing required claim(s)', {
                hasUserId: !!userId,
                hasIntercomJwt: !!intercomJwt,
              });
            }
          }
        } else if (data == null) {
          // Logout — shutdown identified session
          if (this.intercomBootAttempted) {
            this.intercomService.shutdown();
            this.intercomBootAttempted = false;
          }
        }
      });
    }
  }

  @HostListener('window:scroll', ['$event'])
  onWindowScroll($event) {
    const doc = document.documentElement;
    const top = (window.pageYOffset || doc.scrollTop) - (doc.clientTop || 0);

    if (top > 0) {
      this.pageHasScroll = true;
    } else {
      this.pageHasScroll = false;
    }
  }

  private setHelpPath(helpPath: string) {
    this.helpUrl = helpPath;
  }

  private getRouteHelpPath(): Observable<string> {
    let childRoute = this.route.root;

    while (childRoute.firstChild) {
      childRoute = childRoute.firstChild;
    }

    return childRoute.data.pipe(
      map(
        data =>
          easyclaHelp.easyClaHelpPaths[data.pageId] ||
          easyclaHelp.easyClaHelpPaths[PageIds.Faq]
      )
    );
  }

  private initLFXHeaderLinks() {
    const header: any = document.getElementById('lfx-header');

    if (header) {
      header.docslink = generalConstants.header.docslink;
      header.onsupportclick = () => {
        if (window.Intercom) {
          window.Intercom('show');
        }
      };
    }
  }
}
